import React, { useEffect, useState } from 'react';
import {
  createCheckoutSession,
  devActivateSubscription,
  getActivationStatus,
  getWorkspace,
  logout,
  reconcileCheckout,
} from '../api';
import type { Workspace } from '../types';
import './PaymentPendingPage.css';

type Props = {
  workspace: Workspace;
  onLogout: () => void;
  onPaymentConfirmed: (_workspace: Workspace) => void;
};

function PaymentPendingPage({ workspace, onLogout, onPaymentConfirmed }: Props) {
  const [planCode, setPlanCode] = useState<string>(workspace.plan_code || '');
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [loadingRetry, setLoadingRetry] = useState(false);
  const [loadingLogout, setLoadingLogout] = useState(false);
  const [loadingDevActivate, setLoadingDevActivate] = useState(false);
  const [error, setError] = useState('');

  // react-scripts sets NODE_ENV to "development" under `npm start` and
  // "production" for `npm run build`. The button below is a convenience for
  // local development; the backend independently gates the endpoint on
  // settings.DEBUG, so it can never activate against production.
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (planCode) return;
    getActivationStatus(workspace.id)
      .then((status) => {
        if (status.plan_code) {
          setPlanCode(status.plan_code);
        }
      })
      .catch(() => {
        // Keep fallback behavior if status endpoint is unavailable.
      });
  }, [planCode, workspace.id]);

  const handleContinueToStripe = async () => {
    if (!planCode) {
      setError('No plan selected yet. Please refresh and try again.');
      return;
    }

    setError('');
    setLoadingCheckout(true);
    try {
      const origin = window.location.origin;
      const result = await createCheckoutSession({
        workspace_id: workspace.id,
        plan_code: planCode,
        success_url: `${origin}/activate`,
        cancel_url: origin,
      });
      window.location.href = result.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout. Please try again.');
      setLoadingCheckout(false);
    }
  };

  const handleDevActivate = async () => {
    setError('');
    setLoadingDevActivate(true);
    try {
      await devActivateSubscription({
        workspace_id: workspace.id,
        plan_code: planCode || undefined,
      });
      const refreshedWorkspace = await getWorkspace(workspace.id);
      onPaymentConfirmed(refreshedWorkspace);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dev activation failed.');
      setLoadingDevActivate(false);
    }
  };

  const handleLogout = async () => {
    setError('');
    setLoadingLogout(true);
    try {
      await logout();
      onLogout();
    } catch {
      onLogout();
    }
  };

  const handleRetryPaymentCheck = async () => {
    setError('');
    setLoadingRetry(true);

    try {
      const result = await reconcileCheckout({ workspace_id: workspace.id });
      const refreshedWorkspace = await getWorkspace(workspace.id);

      const paymentConfirmed =
        !refreshedWorkspace.is_pending && refreshedWorkspace.plan_status !== 'pending';

      if (paymentConfirmed) {
        onPaymentConfirmed(refreshedWorkspace);
        return;
      }

      const currentStatus = refreshedWorkspace.plan_status || result.status || 'pending';
      setError(
        `Payment is still ${currentStatus}. If you just paid, wait a few seconds and retry.`
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not recheck payment status. Please try again.'
      );
    } finally {
      setLoadingRetry(false);
    }
  };

  return (
    <div className="pay-pending-page">
      <div className="pay-pending-card">
        <div className="pay-pending-icon" aria-hidden="true">
          ⏳
        </div>
        <h1 className="pay-pending-title">Payment pending</h1>
        <p className="pay-pending-copy">
          Your workspace is created, but payment is not completed yet. Finish Stripe checkout to
          unlock your dashboard and AI actions.
        </p>

        {error && (
          <div className="pay-pending-error" role="alert">
            {error}
          </div>
        )}

        <div className="pay-pending-actions">
          <button
            type="button"
            className="pay-pending-btn pay-pending-btn--primary"
            onClick={handleContinueToStripe}
            disabled={loadingCheckout || loadingRetry || loadingLogout || loadingDevActivate || !planCode}
          >
            {loadingCheckout ? 'Redirecting…' : 'Continue to Stripe'}
          </button>
          <button
            type="button"
            className="pay-pending-btn pay-pending-btn--ghost"
            onClick={handleRetryPaymentCheck}
            disabled={loadingCheckout || loadingRetry || loadingLogout || loadingDevActivate}
          >
            {loadingRetry ? 'Checking…' : 'Retry payment check'}
          </button>
          {isDev && (
            <button
              type="button"
              className="pay-pending-btn pay-pending-btn--ghost pay-pending-btn--dev"
              onClick={handleDevActivate}
              disabled={loadingCheckout || loadingRetry || loadingLogout || loadingDevActivate}
            >
              {loadingDevActivate ? 'Activating…' : '⚡ Dev: activate without payment'}
            </button>
          )}
          <button
            type="button"
            className="pay-pending-btn pay-pending-btn--ghost"
            onClick={handleLogout}
            disabled={loadingCheckout || loadingRetry || loadingLogout || loadingDevActivate}
          >
            {loadingLogout ? 'Logging out…' : 'Logout'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentPendingPage;
